import classNames from "classnames"
import {
  differenceInCalendarDays,
  format,
  isSameDay,
  type Locale,
} from "date-fns"
import { useCallback, useMemo } from "react"
import { getMonthDaysByWeeks, getWeekDays } from "../utils"
import type {
  CalendarRangePickerPropsType,
  DayStatusInterface,
  RangeConstraintsInterface,
} from "./CalendarRangePicker"
import { CalendarRangePickerDay } from "./CalendarRangePickerDay"

type CalendarRangePickerMonthPropsType = {
  date: Date
  startDate?: Date
  endDate?: Date
  onSelectDate: (date: Date, isOutOfRange?: boolean) => void
  onChangeDate: (date: Date, status: DayStatusInterface) => void
  onLeaveDate?: (date: Date) => void
  isEditingStartDate?: boolean
  isEditingEndDate?: boolean
  firstAvailableDate: Date
  lastAvailableDate?: Date
  isDayDisabledFn?: CalendarRangePickerPropsType["isDayDisabledFn"]
  hasDayNotificationFn?: CalendarRangePickerPropsType["hasDayNotificationFn"]
  isDayInvalidForSelectionFn?: CalendarRangePickerPropsType["isDayInvalidForSelectionFn"]
  dayHoverTooltipFn?: CalendarRangePickerPropsType["dayHoverTooltipFn"]
  rangeConstraints?: RangeConstraintsInterface
  isSundayFirstDayOfWeek?: boolean
  locale?: Locale
}

function isDisabled(
  day: Date,
  firstAvailableDate: Date,
  lastAvailableDate: Date | undefined,
) {
  return (
    day.getTime() < firstAvailableDate.getTime() ||
    (lastAvailableDate != null && day.getTime() > lastAvailableDate.getTime())
  )
}

function isInvalidOppositeBound({
  isEditingStartDate,
  isEditingEndDate,
  isStartDay,
  isEndDay,
  startDate,
  endDate,
}: {
  startDate: Date | undefined
  endDate: Date | undefined
  isEditingStartDate: boolean | undefined
  isEditingEndDate: boolean | undefined
  isStartDay: boolean
  isEndDay: boolean
}) {
  return (
    startDate != null &&
    endDate != null &&
    startDate > endDate &&
    ((isEditingStartDate && isEndDay) || (isEditingEndDate && isStartDay))
  )
}

function isDayOutOfRange({
  day,
  rangeConstraints,
  startDate,
}: {
  day: Date
  startDate: Date | undefined
  rangeConstraints: RangeConstraintsInterface | undefined
}) {
  return (
    rangeConstraints != null &&
    rangeConstraints.max != null &&
    startDate != null &&
    differenceInCalendarDays(day, startDate) >= rangeConstraints.max.value
  )
}

function isDayInvalid({
  day,
  isDaySelected,
  rangeConstraints,
  rangeTooShort,
  rangeExceeded,
  isEditingStartDate,
  isEditingEndDate,
  isStartDay,
  isEndDay,
  isDayInvalidForSelectionFn,
  startDate,
  endDate,
}: {
  day: Date
  isDaySelected: boolean
  startDate: Date | undefined
  endDate: Date | undefined
  rangeConstraints: RangeConstraintsInterface | undefined
  rangeTooShort: boolean
  rangeExceeded: boolean
  isEditingStartDate: boolean | undefined
  isEditingEndDate: boolean | undefined
  isStartDay: boolean
  isEndDay: boolean
  isDayInvalidForSelectionFn?: CalendarRangePickerPropsType["isDayInvalidForSelectionFn"]
}) {
  if (!isDaySelected) return false

  let isInvalid = false
  if (!isEditingStartDate && !isEditingEndDate) {
    isInvalid = rangeTooShort || rangeExceeded
  } else if (rangeTooShort && (!isStartDay || isEndDay)) {
    isInvalid = true
  } else if (
    isInvalidOppositeBound({
      isEditingStartDate,
      isEditingEndDate,
      isStartDay,
      isEndDay,
      startDate,
      endDate,
    })
  ) {
    isInvalid = true
  } else {
    isInvalid = isDayOutOfRange({
      day,
      rangeConstraints,
      startDate,
    })
  }

  if (
    !isInvalid &&
    isDayInvalidForSelectionFn &&
    ((isEditingStartDate && isStartDay) || (isEditingEndDate && isEndDay))
  ) {
    isInvalid = isDayInvalidForSelectionFn(day)
  }

  return isInvalid
}

function isSelected({
  day,
  startDate,
  endDate,
  isStartDay,
  isEndDay,
  isOutOfRangeStartDate,
  rangeTooShort,
  isEditingStartDate,
  isEditingEndDate,
}: {
  day: Date
  startDate: Date | undefined
  endDate: Date | undefined
  isStartDay: boolean
  isEndDay: boolean
  isOutOfRangeStartDate: boolean
  rangeTooShort: boolean
  isEditingStartDate: boolean
  isEditingEndDate: boolean
}) {
  return !!(
    isStartDay ||
    isEndDay ||
    ((!rangeTooShort || (!isEditingStartDate && !isEditingEndDate)) &&
      !isOutOfRangeStartDate &&
      endDate &&
      day.getTime() <= endDate.getTime() &&
      startDate &&
      day.getTime() > startDate.getTime())
  )
}

function isRangeExceeded(rangeInDays: number, rangeLimit: number | undefined) {
  return rangeLimit != null && rangeInDays > rangeLimit
}

function isRangeTooShort(rangeInDays: number, rangeLimit: number | undefined) {
  return rangeInDays <= 0
    ? false
    : rangeLimit != null && rangeInDays < rangeLimit
}

function buildTooltipMessage({
  rangeInDays,
  isEditingStartDate,
  isStartDay,
  isEditingEndDate,
  isEndDay,
  rangeConstraints,
}: {
  rangeInDays: number
  isEditingStartDate: boolean
  isStartDay: boolean
  isEditingEndDate: boolean
  isEndDay: boolean
  rangeConstraints?: RangeConstraintsInterface
}) {
  let tooltipMessage: string | undefined
  if (
    rangeConstraints &&
    rangeInDays >= 0 &&
    ((isEditingStartDate && isStartDay) || (isEditingEndDate && isEndDay))
  ) {
    if (isRangeTooShort(rangeInDays, rangeConstraints.min?.value)) {
      tooltipMessage = rangeConstraints?.min?.message
    } else if (isRangeExceeded(rangeInDays, rangeConstraints.max?.value)) {
      tooltipMessage = rangeConstraints?.max?.message
    }
  }

  return tooltipMessage
}

function getRangeInDays(range: {
  startDate: Date | undefined
  endDate: Date | undefined
}) {
  return range.startDate && range.endDate
    ? differenceInCalendarDays(range.endDate, range.startDate) + 1
    : 0
}

export function CalendarRangePickerMonth({
  date,
  onSelectDate,
  onChangeDate,
  onLeaveDate,
  startDate,
  endDate,
  isEditingStartDate = false,
  isEditingEndDate = false,
  firstAvailableDate,
  lastAvailableDate,
  isDayDisabledFn,
  isDayInvalidForSelectionFn,
  hasDayNotificationFn,
  dayHoverTooltipFn,
  rangeConstraints,
  isSundayFirstDayOfWeek,
  locale,
}: CalendarRangePickerMonthPropsType) {
  const byWeeks = useMemo(
    () => getMonthDaysByWeeks(date, isSundayFirstDayOfWeek),
    [date, isSundayFirstDayOfWeek],
  )

  const today = new Date().setHours(0, 0, 0, 0)

  const weekdays: Date[] = useMemo(
    () => getWeekDays(isSundayFirstDayOfWeek),
    [isSundayFirstDayOfWeek],
  )

  const rangeInDays = getRangeInDays({ startDate, endDate })

  const rangeExceeded = isRangeExceeded(
    rangeInDays,
    rangeConstraints?.max?.value,
  )

  const rangeTooShort = isRangeTooShort(
    rangeInDays,
    rangeConstraints?.min?.value,
  )

  const isOutOfRangeStartDate = isEditingStartDate && rangeExceeded

  const onMouseEnter = (day: Date, disabled: boolean) => {
    const status: DayStatusInterface = {}

    if (disabled) {
      status.isDisabled = true
    }

    if (!disabled && endDate && startDate) {
      const currentRange = isEditingStartDate
        ? { startDate: day, endDate: endDate }
        : { startDate: startDate, endDate: day }

      const currentRangeInDays = getRangeInDays({
        startDate: currentRange.startDate,
        endDate: currentRange.endDate,
      })

      const currentRangeExceeded = isRangeExceeded(
        currentRangeInDays,
        rangeConstraints?.max?.value,
      )

      if (currentRangeExceeded) {
        status.isRangeExceeded = true
      } else {
        const currentRangeTooShort = isRangeTooShort(
          currentRangeInDays,
          rangeConstraints?.min?.value,
        )

        if (currentRangeTooShort) {
          status.isRangeTooShort = true
        } else {
          status.isCustomInvalid = isDayInvalidForSelectionFn?.(day)
        }
      }
    }

    onChangeDate(day, status)
  }

  const onSelectDateCallback = useCallback(
    (date: Date) => {
      if (isEditingStartDate) {
        onSelectDate(
          date,
          isRangeExceeded(
            getRangeInDays({ startDate: date, endDate }),
            rangeConstraints?.max?.value,
          ),
        )
      } else {
        onSelectDate(
          date,
          isRangeExceeded(
            getRangeInDays({ startDate, endDate: date }),
            rangeConstraints?.max?.value,
          ),
        )
      }
    },
    [
      isEditingStartDate,
      onSelectDate,
      rangeConstraints?.max?.value,
      startDate,
      endDate,
    ],
  )

  return (
    <div
      className={classNames("cobalt-CalendarRangePicker__month", {
        "cobalt-CalendarRangePicker__month--invalid":
          rangeExceeded && !isEditingStartDate,
      })}
      data-month={format(date, "yyyy-MM")}
    >
      <div className="cobalt-CalendarRangePicker__month-header">
        {format(date, "MMMM yyyy", { locale })}
      </div>
      <div className="cobalt-CalendarRangePicker__month__weeks-container">
        <div className="cobalt-CalendarRangePicker__month__week-header">
          {weekdays.map((weekday) => (
            <div
              key={weekday.getTime()}
              className="cobalt-CalendarRangePicker__month__day-header"
            >
              {format(weekday, "iiiiii", { locale })}
            </div>
          ))}
        </div>
        {byWeeks.map((week, index) => (
          <div
            key={`week-${index}-${week.map((day) => day.getTime()).join("-")}`}
            className="cobalt-CalendarRangePicker__month__week"
          >
            {week.map((day) => {
              const isStartDay = startDate != null && isSameDay(day, startDate)
              const isEndDay = endDate != null && isSameDay(day, endDate)

              const disabled =
                isDisabled(day, firstAvailableDate, lastAvailableDate) ||
                isDayDisabledFn?.(day)

              const hasNotification =
                !disabled &&
                hasDayNotificationFn != null &&
                hasDayNotificationFn(day)

              const isRange =
                rangeInDays !== 0 &&
                (isStartDay || isEndDay) &&
                (!rangeTooShort || (!isEditingStartDate && !isEditingEndDate))

              const isDaySelected = isSelected({
                day,
                startDate,
                endDate,
                isStartDay,
                isEndDay,
                isOutOfRangeStartDate,
                rangeTooShort,
                isEditingStartDate,
                isEditingEndDate,
              })

              const tooltip = buildTooltipMessage({
                rangeInDays,
                isEditingStartDate: !!isEditingStartDate,
                isEditingEndDate: !!isEditingEndDate,
                isStartDay,
                isEndDay,
                rangeConstraints,
              })

              return (
                <CalendarRangePickerDay
                  key={day.getTime()}
                  date={day}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onLeaveDate}
                  onSelect={onSelectDateCallback}
                  isToday={isSameDay(day, today)}
                  isActive={
                    (isEditingStartDate && isStartDay) ||
                    (isEditingEndDate && isEndDay)
                  }
                  isStartDay={isStartDay}
                  isEndDay={isEndDay}
                  isRange={isRange}
                  isEditingStartDate={isEditingStartDate}
                  isEditingEndDate={isEditingEndDate}
                  isInvalid={isDayInvalid({
                    day,
                    isDayInvalidForSelectionFn,
                    isDaySelected,
                    startDate,
                    rangeConstraints,
                    rangeTooShort,
                    rangeExceeded,
                    isEditingStartDate,
                    isEditingEndDate,
                    isStartDay,
                    isEndDay,
                    endDate,
                  })}
                  hasNotification={hasNotification}
                  isDisabled={disabled}
                  tooltipMessage={tooltip}
                  hoverTooltipMessage={
                    dayHoverTooltipFn ? dayHoverTooltipFn(day) : undefined
                  }
                  isSelected={isDaySelected}
                />
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
